import Table, { Column, Row } from '@paytrace/pt-skel/Component/Table'
import PubSub from '@paytrace/pt-skel/Event/PubSub'
import React, { useEffect, useState } from 'react'
import Event from '../../Event'
import { Link } from 'react-router-dom'
import { formatMoney } from '../../Helper/formatMoney'
import {
  formatDate,
  RecurringFormData,
} from './SearchPayments'
import { paymentCellStyle, summaryTableColumnsStyles, transactionTable } from './styles'
import { PaymentResults, RecurringPaymentResponse } from './types'
import Pagination from '../../Component/Pagination'
import { downloadCSV } from '../../Helper/DownLoadCSV'
import DisplayFound from './DisplayFound'
import mapPaymentResults from '../../Helper/mapPaymentResults'
import DisplayLayout from './DisplayLayout'
import useGetMerchantSurchargeInfo from '../../hooks/useGetMerchantSurchargeInfo'

const self = Symbol('SearchPayments')
const selfSurcharge = Symbol('SurchargeInfo')

export const TableDataValue = ({
  value,
  style,
}: {
  value: string | number | React.ReactElement;
  style?: React.CSSProperties;
}) => {
  return <div style={style}>{value}</div>
}

function DisplayPayments ({
  formData,
  currentPage,
  setCurrentPage,
}: {
  formData: RecurringFormData;
  currentPage: number;
  setCurrentPage: (value: number) => void;
}) {
  const [paymentResults, setPaymentResults] = useState<PaymentResults[]>([])
  const [paymentListLoaded, setPaymentListLoaded] = useState(false)
  const [paginatedClicked, setPaginationClicked] = useState(false)
  const [downloadClicked, setDownloadClicked] = useState(false)
  const surchargeInfo = useGetMerchantSurchargeInfo(selfSurcharge, formData.merchant_id)
  const isSurchargeEnabled = surchargeInfo?.is_surcharge_enabled ?? false

  useEffect(() => {
    if (paginatedClicked) {
      const newFormData = {
        formData: {
          ...formData,
          dateRange: {
            startDate: formData.dateRange.startDate
              ? formatDate(formData.dateRange.startDate)
              : formatDate(new Date()),
            endDate: formData.dateRange.endDate
              ? formatDate(formData.dateRange.endDate)
              : formatDate(new Date()),
          },
        },
        currentPage,
      }
      PubSub.pub(Event.POST_RECURRING_VIEW_PAYMENT_REQUEST, newFormData)
    }
    if (currentPage === 0) {
      setPaginationClicked(false)
    }
    // Only want to run this effect when currentPage changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage])

  useEffect(() => {
    if (!downloadClicked) {
      const subscription = (data: any) => {
        const results = mapPaymentResults(data.data)
        if (JSON.stringify(results) !== JSON.stringify(paymentResults)) {
          setPaymentResults(results)
        }
        setPaymentListLoaded(true) // Set loaded after the customer list is fetched
      }

      PubSub.sub(self, Event.POST_RECURRING_VIEW_PAYMENT_RESPONSE, subscription)

      return () => {
        PubSub.unsub(Event.POST_RECURRING_VIEW_PAYMENT_RESPONSE)
      }
    }

    return undefined
    // Only need to update when paymentResults or setPaymentResults changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentResults, setPaymentResults])

  useEffect(() => {
    if (downloadClicked) {
      const fetchAllPayments = async () => {
        const newFormData = {
          formData: {
            ...formData,
            dateRange: {
              startDate: formData.dateRange.startDate
                ? formatDate(formData.dateRange.startDate)
                : formatDate(new Date()),
              endDate: formData.dateRange.endDate
                ? formatDate(formData.dateRange.endDate)
                : formatDate(new Date()),
            },
          },
          currentPage: 0,
          limit: 50,
        }

        let allResults: PaymentResults[] = []
        let page = 0
        let hasMore = true

        const getPageData = (pageNumber: number): Promise<RecurringPaymentResponse[]> => {
          return new Promise((resolve) => {
            const responseHandler = (data: any) => {
              PubSub.unsub(Event.POST_RECURRING_VIEW_PAYMENT_RESPONSE)
              resolve(data.data)
            }

            PubSub.pub(Event.GET_RECURRING_PAYMENT_REQUEST, {
              ...newFormData,
              currentPage: pageNumber,
            })
            PubSub.sub(self, Event.GET_RECURRING_PAYMENT_RESPONSE, responseHandler)
          })
        }

        while (hasMore) {
          const pageData = await getPageData(page)
          const results = mapPaymentResults(pageData)
          allResults = [...allResults, ...results]
          hasMore = pageData.length === 50
          page++
        }

        return allResults
      }

      const handleDownloadClick = async () => {
        const allPayments = await fetchAllPayments()
        const headers = [
          'RecurringID',
          'Customer_ID',
          'Customer_Name',
          'Next_Date',
          'Frequency',
          'Current_Count',
          'Total_Count',
          'Amount',
          'Receipt',
          'Description',
        ]
        const fields: (keyof PaymentResults)[] = [
          'id',
          'customerId',
          'customerName',
          'nextDate',
          'frequency',
          'currentCount',
          'totalCount',
          'amount',
          'receipt',
          'description',
        ]

        downloadCSV<PaymentResults>(formData.merchant_id, allPayments, headers, fields)

        setDownloadClicked(false)
      }

      handleDownloadClick()
    }
  }, [downloadClicked, formData, paymentResults])

  const handleEditPayment = (recurringId: string) => {
    return `/modifyrecur.pay?recurid=${recurringId}`
  }

  const handleEditCustomer = (customerId: string) => {
    if (customerId === '') {
      return `/mcust.pay?a=mcust`
    }

    return `/mcust.pay?a=mcust&cid=${customerId}`
  }
  const surchargeStar = isSurchargeEnabled ? '*' : ''

  const paymentColumns: Column[] = [
    {
      field: 'Disabled',
      headerName: '',
      width: '1%',
      cellRender: (_, row: Row) => (
        <>{row.disabled && <span className="label label-danger">!</span>}</>
      ),
    },
    {
      field: 'id',
      headerName: 'ID',
      width: '5%',
      headerRender: () => (
        <TableDataValue
          value="ID"
          style={summaryTableColumnsStyles.depositIdHeaderRender}
        />
      ),
      cellRender: (_, row: Row) => (
        <Link
          to={handleEditPayment(row.id)}
          style={{ cursor: 'pointer', ...paymentCellStyle.cellColorBlue }}
        >
          <TableDataValue value={row.id} />
        </Link>
      ),
    },
    {
      field: 'customerName',
      headerName: 'Customer Name',
      headerRender: () => (
        <TableDataValue
          value="Customer Name"
          style={summaryTableColumnsStyles.depositIdHeaderRender}
        />
      ),
      cellRender: (_, row: Row) => (
        <Link to={handleEditCustomer(row.customerId)}>
          <TableDataValue value={row.customerName} />
        </Link>
      ),
    },
    {
      field: 'nextDate',
      headerName: 'Next Date',
      headerRender: () => (
        <TableDataValue
          value="Next Date"
          style={summaryTableColumnsStyles.dateCellRender}
        />
      ),
      cellRender: (_, row: Row) => <TableDataValue value={row.nextDate} />,
    },
    {
      field: 'frequency',
      headerName: 'Frequency',
      headerRender: () => (
        <TableDataValue
          value="Frequency"
          style={summaryTableColumnsStyles.depositIdHeaderRender}
        />
      ),
      cellRender: (_, row: Row) => <TableDataValue value={row.frequency} />,
    },
    {
      field: 'currentCount',
      headerName: 'Current Count',
      headerRender: () => (
        <TableDataValue
          value="Current Count"
          style={summaryTableColumnsStyles.depositIdHeaderRender}
        />
      ),
      cellRender: (_, row: Row) => <TableDataValue value={row.currentCount} />,
    },
    {
      field: 'totalCount',
      headerName: 'Total Count',
      headerRender: () => (
        <TableDataValue
          value="Total Count"
          style={summaryTableColumnsStyles.depositIdHeaderRender}
        />
      ),
      cellRender: (_, row: Row) => <TableDataValue value={row.totalCount} />,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      headerRender: () => (
        <TableDataValue
          value={`${surchargeStar}Amount`}
          style={summaryTableColumnsStyles.depositIdHeaderRender}
        />
      ),
      cellRender: (_, row: Row) => (
        <TableDataValue value={formatMoney(row.amount)} />
      ),
    },
    ...(isSurchargeEnabled
      ? [
          {
            field: 'surcharge',
            headerName: 'Surcharge',
            headerRender: () => (
              <TableDataValue
                value="Surcharge"
                style={summaryTableColumnsStyles.depositIdHeaderRender}
              />
            ),
            cellRender: (_: any, row: Row) => (
              <TableDataValue value={row.addSurcharge ? 'Enabled' : 'Disabled'} />
            ),
          },
        ]
      : []),
    {
      field: 'receipt',
      headerName: 'Receipt',
      headerRender: () => (
        <TableDataValue
          value="Receipt"
          style={summaryTableColumnsStyles.depositIdHeaderRender}
        />
      ),
      cellRender: (_, row: Row) => (
        <TableDataValue value={row.receipt === 'Y' ? 'Yes' : 'No'} />
      ),
    },
  ]

  if (!paymentListLoaded) {
    return <></>
  }

  return (
    <DisplayLayout
      isDownloadClicked={paymentResults.length > 0}
      setDownloadClicked={setDownloadClicked}
      style={{ padding: '12px 15px' }}
    >
      <Table
        cssStyles={transactionTable}
        data={paymentResults.length ? paymentResults : []}
        columns={paymentColumns}
      />
      <Pagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        noOfRows={paymentResults.length}
        limit={50}
        setClicked={setPaginationClicked}
      />

      <DisplayFound paymentResults={paymentResults} />
      {isSurchargeEnabled && (
        <div style={{ marginTop: '10px' }}>
            * Amount does not include surcharge
        </div>
      )}
    </DisplayLayout>
  )
}

export default DisplayPayments
